import React, { useRef, useState, useCallback } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Paper,
  ButtonGroup,
  Button,
} from '@mui/material';
import {
  Close as CloseIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';

const isDirectVideoUrl = (url: string): boolean =>
  /\.(mp4|webm|ogg|mov|avi|mkv)(\?.*)?$/i.test(url);

const isYouTubeUrl = (url: string): boolean => /youtube\.com|youtu\.be/.test(url);

const getYouTubeVideoId = (url: string): string | null => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) return match[1];
  }
  return null;
};

const getYouTubeStartTime = (url: string): number | null => {
  const tMatch = url.match(/[?&]t=([^&\n?#]+)/i);
  if (!tMatch || !tMatch[1]) return null;
  const timeString = tMatch[1].toLowerCase();
  if (/^\d+$/.test(timeString)) return parseInt(timeString, 10);
  if (timeString.endsWith('s') && /^\d+s?$/.test(timeString)) {
    return parseInt(timeString.replace('s', ''), 10);
  }
  let totalSeconds = 0;
  const hoursMatch = timeString.match(/(\d+)h/);
  if (hoursMatch) totalSeconds += parseInt(hoursMatch[1], 10) * 3600;
  const minutesMatch = timeString.match(/(\d+)m/);
  if (minutesMatch) totalSeconds += parseInt(minutesMatch[1], 10) * 60;
  const secondsMatch = timeString.match(/(\d+)s(?!\d)/);
  if (secondsMatch) totalSeconds += parseInt(secondsMatch[1], 10);
  return hoursMatch || minutesMatch || secondsMatch ? totalSeconds : null;
};

interface SideBySideVideoPlayerProps {
  referenceVideoUrl: string;
  playerVideoUrl: string;
  onClose: () => void;
  referenceLabel?: string;
  playerLabel?: string;
}

const SPEED_OPTIONS = [
  { value: 1, label: '1x' },
  { value: 0.5, label: '0.5x' },
  { value: 0.25, label: '0.25x' },
];

/**
 * Side-by-side video comparison: reference (left) vs player upload (right).
 * Synced play/pause and shared playback rate for slow-motion comparison.
 * Best with direct video URLs (mp4, etc.); YouTube URLs are not synced.
 */
const SideBySideVideoPlayer: React.FC<SideBySideVideoPlayerProps> = ({
  referenceVideoUrl,
  playerVideoUrl,
  onClose,
  referenceLabel = 'Reference',
  playerLabel = 'Your video',
}) => {
  const leftRef = useRef<HTMLVideoElement>(null);
  const rightRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [youtubeKey, setYoutubeKey] = useState(0);

  const refLeft = referenceVideoUrl?.trim() || '';
  const refRight = playerVideoUrl?.trim() || '';
  const leftIsYouTube = isYouTubeUrl(refLeft);
  const leftIsDirect = isDirectVideoUrl(refLeft);
  const rightIsDirect = isDirectVideoUrl(refRight);

  const applyRate = useCallback((rate: number) => {
    [leftRef.current, rightRef.current].forEach((el) => {
      if (el) el.playbackRate = rate;
    });
  }, []);

  const handlePlayPause = useCallback(() => {
    if (!rightIsDirect) {
      setError('Your video must be a direct file (e.g. MP4) for play/pause and slow motion.');
      return;
    }
    setError(null);
    const right = rightRef.current;
    if (!right) return;
    if (leftIsDirect) {
      const left = leftRef.current;
      if (left) {
        if (playing) {
          left.pause();
          right.pause();
        } else {
          left.play().catch(() => {});
          right.play().catch(() => {});
        }
      }
    } else {
      if (playing) right.pause();
      else right.play().catch(() => {});
    }
    setPlaying(!playing);
  }, [playing, leftIsDirect, rightIsDirect]);

  const handleSpeed = useCallback((rate: number) => {
    setSpeed(rate);
    applyRate(rate);
  }, [applyRate]);

  const handleCanPlay = useCallback(() => {
    applyRate(speed);
    // When reference is YouTube (autoplaying), start the player video too so both play together
    if (leftIsYouTube && rightIsDirect && rightRef.current) {
      rightRef.current.play().catch(() => {});
      setPlaying(true);
    }
  }, [speed, applyRate, leftIsYouTube, rightIsDirect]);

  const handleResetToStart = useCallback(() => {
    if (leftIsYouTube) {
      setYoutubeKey((k) => k + 1);
    } else if (leftRef.current) {
      leftRef.current.currentTime = 0;
    }
    if (rightRef.current) {
      rightRef.current.currentTime = 0;
    }
    setPlaying(false);
  }, [leftIsYouTube]);

  if (!refLeft || !refRight) {
    return (
      <Paper elevation={8} sx={{ p: 2 }}>
        <Typography color="error">Missing reference or player video URL.</Typography>
        <Button onClick={onClose} sx={{ mt: 1 }}>Close</Button>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        width: 720,
        maxWidth: 'calc(100vw - 32px)',
        zIndex: 1300,
        overflow: 'hidden',
        bgcolor: '#000',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: 0.5, bgcolor: 'rgba(0,0,0,0.9)', color: '#fff' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Compare: Reference vs Your video</Typography>
        <IconButton size="small" sx={{ color: '#fff' }} onClick={onClose} title="Close">
          <CloseIcon />
        </IconButton>
      </Box>
      {error && (
        <Box sx={{ px: 1.5, py: 0.5, bgcolor: 'rgba(255,152,0,0.2)', color: '#e65100' }}>
          <Typography variant="caption">{error}</Typography>
        </Box>
      )}
      {leftIsYouTube && !error && (
        <Box sx={{ px: 1.5, py: 0.25, bgcolor: 'rgba(255,255,255,0.08)' }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>Reference (YouTube) starts automatically (muted). Unmute in the player; Play/Pause and speed below apply to your video.</Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0 }}>
        <Box sx={{ flex: 1, position: 'relative', bgcolor: '#111' }}>
          <Typography variant="caption" sx={{ position: 'absolute', top: 4, left: 4, zIndex: 1, bgcolor: 'rgba(0,0,0,0.7)', color: '#fff', px: 0.5 }}>{referenceLabel}</Typography>
          {leftIsYouTube ? (() => {
            const videoId = getYouTubeVideoId(refLeft);
            if (!videoId) return (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">Invalid YouTube link</Typography>
                <Typography component="a" href={refLeft} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ color: 'primary.main' }}>Open</Typography>
              </Box>
            );
            const startTime = getYouTubeStartTime(refLeft);
            let embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1`;
            if (startTime != null && startTime > 0) embedUrl += `&start=${startTime}`;
            return (
              <Box sx={{ position: 'relative', width: '100%', paddingBottom: '56.25%', height: 0, maxHeight: 320 }}>
                <iframe
                  key={youtubeKey}
                  title={referenceLabel}
                  src={embedUrl}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 0,
                  }}
                />
              </Box>
            );
          })() : leftIsDirect ? (
            <video
              ref={leftRef}
              src={refLeft}
              controls={false}
              playsInline
              onCanPlay={handleCanPlay}
              onEnded={() => setPlaying(false)}
              style={{ width: '100%', display: 'block', maxHeight: 320, objectFit: 'contain' }}
            />
          ) : (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">Reference link (open in new tab for playback)</Typography>
              <Typography component="a" href={refLeft} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ color: 'primary.main' }}>Open reference</Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ flex: 1, position: 'relative', bgcolor: '#111' }}>
          <Typography variant="caption" sx={{ position: 'absolute', top: 4, left: 4, zIndex: 1, bgcolor: 'rgba(0,0,0,0.7)', color: '#fff', px: 0.5 }}>{playerLabel}</Typography>
          {rightIsDirect ? (
            <video
              ref={rightRef}
              src={refRight}
              controls={false}
              playsInline
              onCanPlay={handleCanPlay}
              onEnded={() => setPlaying(false)}
              style={{ width: '100%', display: 'block', maxHeight: 320, objectFit: 'contain' }}
            />
          ) : (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">Player link (open in new tab)</Typography>
              <Typography component="a" href={refRight} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ color: 'primary.main' }}>Open video</Typography>
            </Box>
          )}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'rgba(0,0,0,0.85)', color: '#fff', flexWrap: 'wrap' }}>
        <IconButton size="small" sx={{ color: '#fff' }} onClick={handlePlayPause} title={playing ? 'Pause' : 'Play'} disabled={!rightIsDirect}>
          {playing ? <PauseIcon /> : <PlayIcon />}
        </IconButton>
        <IconButton size="small" sx={{ color: '#fff' }} onClick={handleResetToStart} title="Reset to beginning">
          <ReplayIcon />
        </IconButton>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>Reset</Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>Speed (your video):</Typography>
        <ButtonGroup size="small" sx={{ '& .MuiButton-root': { color: 'rgba(255,255,255,0.9)', borderColor: 'rgba(255,255,255,0.3)' } }}>
          {SPEED_OPTIONS.map((opt) => (
            <Button
              key={opt.value}
              variant={speed === opt.value ? 'contained' : 'outlined'}
              onClick={() => handleSpeed(opt.value)}
              disabled={!rightIsDirect}
              sx={{ minWidth: 48 }}
            >
              {opt.label}
            </Button>
          ))}
        </ButtonGroup>
      </Box>
    </Paper>
  );
};

export default SideBySideVideoPlayer;
